
import { defineComponent, computed } from 'vue';
import BalLoadingIcon from '../BalLoadingIcon/BalLoadingIcon.vue';

export default defineComponent({
  name: 'BalBtn',

  components: {
    BalLoadingIcon
  },

  props: {
    tag: {
      type: String,
      default: 'button',
      validator: (val: string): boolean =>
        ['button', 'a', 'div', 'router-link'].includes(val)
    },
    size: {
      type: String,
      default: 'md',
      validator: (val: string): boolean =>
        ['xs', 'sm', 'md', 'lg'].includes(val)
    },
    color: {
      type: String,
      default: 'primary',
      validator: (val: string): boolean =>
        [
          'primary',
          'gradient',
          'gradient-reverse',
          'gradient-pink-yellow',
          'gray',
          'red',
          'yellow',
          'white',
          'blue',
          'sky',
          'sky-dark'
        ].includes(val)
    },
    label: { type: String, default: '' },
    block: { type: Boolean, default: false },
    circle: { type: Boolean, default: false },
    outline: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    loadingLabel: { type: String, default: 'loading...' },
    cursor: { type: String, default: 'pointer' },
    border: { type: String, default: 'normal' },
    disabled: { type: Boolean, default: false }
  },

  setup(props) {
    const sizeClasses = computed(() => {
      switch (props.size) {
        case 'xs':
          return 'px-2 h-6 text-xs';
        case 'sm':
          return 'px-3 h-8 text-sssm lg:text-sm font-medium';
        case 'lg':
          return 'px-5 h-18 text-lg md:text-2xl';
        default:
          return 'px-4 h-12 text-base';
      }
    });

    const circleSizeClasses = computed(() => {
      switch (props.size) {
        case 'xs':
          return 'w-6 h-6 text-sssm';
        case 'sm':
          return 'w-8 h-8 text-sssm lg:text-sm font-medium';
        case 'md':
          return 'w-12 h-12 text-lg';
        case 'lg':
          return 'w-16 h-16 text-2xl';
        default:
          return 'w-12 h-12 text-base';
      }
    });

    const bgGradientClasses = computed(() => {
      if (props.outline) return 'bg-transparent';

      let fromColor = 'blue';
      let toColor = 'pink';

      if (props.color === 'gradient-reverse') {
        fromColor = 'pink';
        toColor = 'blue';
      } else if (props.color === 'gradient-pink-yellow') {
        fromColor = 'pink';
        toColor = 'yellow';
      }

      if (props.disabled) {
        return `bg-gray-300 dark:bg-gray-700 text-white dark:text-gray-500`;
      }
      if (props.loading) {
        return `bg-gradient-to-tr from-${fromColor}-300 to-${toColor}-300`;
      }
      return `
        bg-gradient-to-tr from-${fromColor}-500 to-${toColor}-500
        hover:from-${fromColor}-600 hover:to-${toColor}-600
      `;
    });

    const bgColorClasses = computed(() => {
      if (props.color.includes('gradient')) return bgGradientClasses.value;
      else if (props.outline) return 'bg-transparent hover:opacity-70';
      else if (props.color === 'white') {
        return 'bg-white';
      } else {
        if (props.disabled) {
          return `bg-gray-300 dark:bg-gray-700 text-white dark:text-gray-500`;
        }

        if (props.color === 'sky') {
          return `bg-${props.color}-320 hover:opacity-70`;
        }

        if (props.color === 'sky-dark') {
          return `bg-sky-420 hover:opacity-70`;
        }

        if (props.color === 'yellow') {
          return `bg-${props.color}-550 hover:opacity-70`;
        }

        if (props.color === 'gray') {
          return `bg-white hover:opacity-70 dark:bg-${props.color}-650`;
        }

        if (props.loading) {
          return `bg-${props.color}-400 dark:bg-${props.color}-dark-400`;
        }

        if (props.color === 'red') {
          return `hover:opacity-70 bg-${props.color}-520
          `;
        }

        return `
          bg-${props.color}-500 hover:bg-${props.color}-600
          dark:bg-${props.color}-dark-500 dark:hover:bg-${props.color}-dark-600
        `;
      }
    });

    const borderClasses = computed(() => {
      if (props.outline) {
        if (props.color === 'gray') {
          if (props.border === 'thin') {
            return `border border-${props.color}-450`;
          }
          return `border-2 border-${props.color}-130 dark:border-${props.color}-450`;
        }
        if (props.color === 'yellow') {
          return `border-2 border-${props.color}-550`;
        }
        if (props.color === 'sky') {
          return `border border-${props.color}-320`;
        }
        if (props.color === 'sky-dark') {
          return `border border-sky-420`;
        }

        return `border border-${props.color}-200 dark:border-${props.color}-700`;
      }
      if (props.color === 'gray') {
        return `border border-${props.color}-130 dark:border-${props.color}-650`;
      }
      if (props.color === 'white') {
        return `border-2 dark:border-0 border-gray-130`;
      }
      return 'border-none';
    });

    const textColorClasses = computed(() => {
      if (props.color === 'white') {
        if (props.outline) return 'text-white';
        else return 'text-gray-800 dark:text-gray-100';
      }
      if (props.color === 'gray') {
        return 'text-gray-670 dark:text-white';
      }
      if (props.outline) {
        if (props.color === 'yellow') {
          return `text-${props.color}-550`;
        }
        if (props.color === 'sky') {
          return `text-${props.color}-320`;
        }
        if (props.color === 'sky-dark') {
          return `text-sky-420`;
        }

        return `text-${props.color}-500 dark:text-${props.color}-400`;
      }
      return 'text-white';
    });

    const displayClasses = computed(() => {
      if (props.circle) return 'flex justify-center items-center';
      if (props.block) return 'block w-full';
      return 'inline-block';
    });

    const shapeClasses = computed(() => {
      if (props.circle || props.rounded) return 'rounded-full';
      return 'rounded-lg';
    });

    const cursorClasses = computed(() => {
      if (props.disabled || props.loading) return 'cursor-not-allowed';
      return `cursor-${props.cursor}`;
    });

    // const shadowClasses = computed(() => {
    //   if (props.disabled || props.loading) return '';
    //   if (props.size === 'sm') return 'shadow hover:shadow-none';
    //   return 'shadow hover:shadow-none';
    // });

    const btnClasses = computed(() => {
      return {
        [sizeClasses.value]: !props.circle,
        [circleSizeClasses.value]: props.circle,
        [bgColorClasses.value]: true,
        [textColorClasses.value]: true,
        [borderClasses.value]: true,
        [displayClasses.value]: true,
        [shapeClasses.value]: true,
        // [shadowClasses.value]: true,
        [cursorClasses.value]: true
      };
    });

    const iconColor = computed(() => {
      if (props.outline) return props.color;
      if (props.color === 'white') return 'gray';
      return 'white';
    });

    return {
      btnClasses,
      iconColor
    };
  }
});
